import React from 'react'
import PropTypes from 'prop-types'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import LinearProgress from '@mui/material/LinearProgress'
import { useNavigate } from 'react-router'
import { compact, isNil } from 'lodash'
import { ALIGN, CardTable } from 'common/components/datatable/Table'
import { getDate, getDayMonth } from 'common/utils/DateUtil'
import { formatMilliers, truncateString } from 'common/utils/StringUtil'
import { progressBarSx, statusColor, ticketsLabel } from './LotsUtils'
import { DueDate, MissingBcIcon, StatusChip } from './LotsCommon'

// Pastille compacte d'une note (date + %), colorée selon le statut.
const StepPill = ({ note, onClick }) => {
    const c = statusColor(note.statut)
    return (
        <Box onClick={onClick} sx={{ cursor: 'pointer', flexShrink: 0, borderLeft: `3px solid ${c}`, backgroundColor: `${c}22`, borderRadius: '3px', px: 0.75, py: 0.25, lineHeight: 1.2, textAlign: 'center' }}>
            <Box sx={{ fontSize: 11, fontWeight: 700, color: '#33405c' }}>{getDayMonth(note.date)}</Box>
            <Box sx={{ fontSize: 10, color: '#6b7893' }}>{`${note.avancement}%`}</Box>
        </Box>
    )
}
StepPill.propTypes = { note: PropTypes.shape({ statut: PropTypes.number, date: PropTypes.number, avancement: PropTypes.number }), onClick: PropTypes.func }

const NOTE_MAX_LENGTH = 60

// Cellule « Étapes » : toutes les notes de l'année sélectionnée (pas de repli), la plus
// récente développée. Wrap si nombreuses.
const StepsCell = ({ lot, onEditNote }) => {
    const notes = [...(lot.notes ?? [])].sort((a, b) => a.date - b.date)
    if (!notes.length) {
        return <Typography sx={{ fontSize: 12, color: '#98a3b8' }}>Aucune note</Typography>
    }
    const last = notes[notes.length - 1]
    const earlier = notes.slice(0, -1)
    const c = statusColor(last.statut)

    return (
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5, minWidth: 0, flexWrap: 'wrap' }}>
            {earlier.map((n, i) => <StepPill key={n.id ?? i} note={n} onClick={() => onEditNote(lot, n)} />)}
            <Box onClick={() => onEditNote(lot, last)} sx={{ cursor: 'pointer', flex: '1 1 200px', minWidth: 160, borderLeft: `3px solid ${c}`, backgroundColor: `${c}22`, borderRadius: '3px', px: 1, py: 0.25 }}>
                <Box sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    <b style={{ color: '#1c2340' }}>{`${getDate(last.date)} · ${last.avancement}%`}</b>
                    <span style={{ color: '#98a3b8', marginLeft: 6 }}>dernière note</span>
                </Box>
                <Tooltip title={(last.commentaire ?? '').length > NOTE_MAX_LENGTH ? <span style={{ whiteSpace: 'pre-line' }}>{last.commentaire}</span> : ''}>
                    <Box sx={{ fontSize: 11, color: '#33405c', whiteSpace: 'nowrap' }}>{truncateString(last.commentaire ?? '', NOTE_MAX_LENGTH) || ' '}</Box>
                </Tooltip>
            </Box>
        </Box>
    )
}
StepsCell.propTypes = { lot: PropTypes.object, onEditNote: PropTypes.func }

// Nom du chef de projet sur 2 lignes : prénom puis nom (colonne étroite).
const ChefCell = ({ name = '' }) => {
    const [firstName, ...lastName] = name.split(' ')
    return (
        <Box sx={{ fontSize: 13, lineHeight: 1.3 }}>
            <div>{firstName}</div>
            <div>{lastName.join(' ')}</div>
        </Box>
    )
}
ChefCell.propTypes = { name: PropTypes.string }

const LOT_NAME_MAX_LENGTH = 70

const LOTS_HEADERS = [
    { key: 'nom', value: 'Lot' },
    { key: 'chef', value: 'CdP' },
    { key: 'statut', value: 'Statut' },
    { key: 'avancement', value: 'Avancement' },
    { key: 'echeance', value: 'Échéance' },
    { key: 'devisBc', value: 'Devis / BC' },
    { key: 'total', value: 'Total HT' },
    { key: 'etapes', value: 'Étapes', filterable: false },
]

const IDEAS_HEADERS = LOTS_HEADERS.filter(h => !['chef', 'echeance'].includes(h.key))

// Vue tableau via le composant maison NewTable (wrapper CardTable). Le filtrage colonne est
// désactivé (filtre déjà porté par l'encart au-dessus) ; on garde le tri par colonne via sortValue.
const LotsTable = ({ lots, ideas = false, onEdit, onAddNote, onEditNote, onDelete }) => {
    const navigate = useNavigate()
    const rows = lots.map(lot => {
        const avct = lot.avancement ?? 0
        return {
            lotRef: lot,
            nom: {
                sortValue: lot.nom,
                onClick: () => onEdit(lot),
                value: (
                    <Box sx={{ minWidth: 0, cursor: 'pointer' }}>
                        <Tooltip title={lot.nom.length > LOT_NAME_MAX_LENGTH ? lot.nom : ''}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1c2340', whiteSpace: 'nowrap' }}>{truncateString(lot.nom, LOT_NAME_MAX_LENGTH)}</Typography>
                        </Tooltip>
                        <Typography sx={{ fontSize: 11, color: '#6b7893' }}>{compact([lot.clientName, ideas && lot.chefName, ticketsLabel(lot)]).join(' · ')}</Typography>
                    </Box>
                ),
            },
            chef: { sortValue: lot.chefName ?? '', value: <ChefCell name={lot.chefName ?? ''} /> },
            statut: { sortValue: lot.statut, value: <StatusChip statut={lot.statut} /> },
            avancement: {
                sortValue: avct,
                value: (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ flex: 1 }}><LinearProgress variant='determinate' value={avct} sx={progressBarSx(avct)} /></Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, width: 34, textAlign: 'right' }}>{`${avct}%`}</Typography>
                    </Box>
                ),
            },
            etapes: <StepsCell lot={lot} onEditNote={onEditNote} />,
            echeance: {
                sortValue: lot.echeance ?? 0,
                value: isNil(lot.echeance) ? '' : <DueDate lot={lot} />,
            },
            devisBc: {
                sortValue: lot.devis ?? '',
                value: (
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <Box>
                            {lot.devis && <Typography sx={{ fontSize: 12, color: '#33405c', whiteSpace: 'nowrap' }}>{`Devis ${lot.devis}`}</Typography>}
                            {lot.bonCommande && <Typography sx={{ fontSize: 12, color: '#33405c', whiteSpace: 'nowrap' }}>{`BC ${lot.bonCommande}`}</Typography>}
                        </Box>
                        {!ideas && !lot.bonCommande && <MissingBcIcon />}
                    </Stack>
                ),
            },
            total: {
                sortValue: lot.total ?? -1,
                align: ALIGN.RIGHT,
                value: <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1c2340', textAlign: 'right' }}>{isNil(lot.total) ? '—' : `${formatMilliers(lot.total)} €`}</Typography>,
            },
        }
    })

    const contextActions = (row) => [
        { icon: 'business_center', label: 'Accéder au client', onClick: () => navigate(`/customer/${row.lotRef.idClient}/dashboard`) },
        { icon: 'add_comment', label: 'Ajouter une note', onClick: () => onAddNote(row.lotRef) },
        { icon: 'edit', label: 'Modifier le lot', onClick: () => onEdit(row.lotRef) },
        { icon: 'delete', label: 'Supprimer le lot', color: 'red', onClick: () => onDelete(row.lotRef) },
    ]

    return (
        <CardTable
            title={ideas ? 'Idées' : 'Lots'}
            color='#161832'
            rows={rows}
            headers={ideas ? IDEAS_HEADERS : LOTS_HEADERS}
            contextActions={contextActions}
            filterable={false}
        />
    )
}
LotsTable.propTypes = {
    lots: PropTypes.arrayOf(PropTypes.shape({})),
    ideas: PropTypes.bool,
    onEdit: PropTypes.func, onAddNote: PropTypes.func, onEditNote: PropTypes.func, onDelete: PropTypes.func,
}

export default LotsTable
